/**
 * OwnerInsights — the "Insights" tab on the Dashboard. Pulls real
 * customer-side engagement data from `analytics_events` (the same
 * pipeline that already powers Pro peak-hour insights) and renders
 * 6 headline counters + a 30-day sparkline + top-5 events with CTR.
 *
 * Not to be confused with the platform-level "App Analytics" surface
 * on app.emergent.sh — that one counts every HTTP request including
 * bots. THIS surface is real product engagement only: pin views,
 * profile opens, directions taps, share opens, inquiry submissions.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
  MapPinSimple, Eye, NavigationArrow, ShareNetwork, ChatTeardrop,
  Cursor, ArrowUp, ArrowDown, Heart, Sparkle, ChartLine,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const HEADLINE_META = {
  pin_impression: {
    label: "Pin impressions",
    hint: "Times your pin appeared on a customer's map.",
    Icon: MapPinSimple,
    tone: "text-slate-700",
  },
  pin_click: {
    label: "Pin taps",
    hint: "Customers who tapped your pin to peek.",
    Icon: Cursor,
    tone: "text-indigo-700",
  },
  profile_view: {
    label: "Profile views",
    hint: "Detail sheet opened (the deeper signal).",
    Icon: Eye,
    tone: "text-primary",
  },
  directions_click: {
    label: "Directions",
    hint: "Tapped the orange Directions button — strongest intent.",
    Icon: NavigationArrow,
    tone: "text-emerald-700",
  },
  share_view: {
    label: "Share opens",
    hint: "Visits via a shared link / Story post.",
    Icon: ShareNetwork,
    tone: "text-amber-700",
  },
  event_inquiry: {
    label: "Inquiries",
    hint: "Catering / private-event inquiries received.",
    Icon: ChatTeardrop,
    tone: "text-rose-700",
  },
};

function Delta({ pct, prev }) {
  if (pct === null || pct === undefined) {
    if (prev === 0) return <span className="text-[10px] text-muted-foreground font-medium">new</span>;
    return null;
  }
  if (pct === 0) return <span className="text-[10px] text-muted-foreground font-medium">flat</span>;
  const up = pct > 0;
  return (
    <span className={"inline-flex items-center text-[10px] font-bold " + (up ? "text-emerald-700" : "text-rose-700")}>
      {up ? <ArrowUp weight="bold" className="h-3 w-3" /> : <ArrowDown weight="bold" className="h-3 w-3" />}
      {Math.abs(pct)}%
    </span>
  );
}

function Sparkline({ data }) {
  // CSS-only sparkline — width-100% SVG with normalized y values so it
  // scales to whatever card width the parent gives us. No chart lib
  // dependency; this is the only chart on the surface so it pays its
  // own way.
  const max = Math.max(1, ...data.map((d) => d.profile_view));
  const w = 100;
  const h = 32;
  const step = data.length > 1 ? w / (data.length - 1) : w;
  const points = data
    .map((d, i) => `${(i * step).toFixed(2)},${(h - (d.profile_view / max) * h).toFixed(2)}`)
    .join(" ");
  const lastTen = data.slice(-10);
  const recentSum = lastTen.reduce((a, d) => a + d.profile_view, 0);
  const prevTen = data.slice(-20, -10);
  const prevSum = prevTen.reduce((a, d) => a + d.profile_view, 0);
  const trendUp = recentSum >= prevSum;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          30-day profile-view trend
        </div>
        <div className={"text-[11px] font-semibold " + (trendUp ? "text-emerald-700" : "text-rose-700")}>
          last 10d: {recentSum} {trendUp ? "↑" : "↓"}
        </div>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-12" data-testid="insights-sparkline">
        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className="text-primary"
          points={points}
        />
      </svg>
    </div>
  );
}

export default function OwnerInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/owner/insights");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load insights");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totalEngagement = useMemo(() => {
    if (!data?.headline) return 0;
    return ["pin_click", "profile_view", "directions_click", "share_view", "event_inquiry"]
      .reduce((sum, k) => sum + (data.headline[k]?.cur || 0), 0);
  }, [data]);

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground py-8" data-testid="insights-loading">
        Crunching your numbers…
      </div>
    );
  }
  if (err) {
    return (
      <div className="text-sm text-destructive py-8" data-testid="insights-error">{err}</div>
    );
  }
  if (!data) return null;

  const allZero = totalEngagement === 0 && data.unique_visitors === 0 && data.followers === 0;

  return (
    <section className="space-y-6" data-testid="insights-tab">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ChartLine weight="duotone" className="h-5 w-5 text-primary" /> Your insights
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Real customer engagement over the last {data.window_days} days — pulled live from your traffic.
            Compared to the prior {data.window_days} days for trend arrows.
          </p>
        </div>
        <div className="text-xs text-muted-foreground inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <Heart weight="duotone" className="h-3.5 w-3.5 text-rose-500" />
            <strong className="text-foreground">{data.followers}</strong> followers
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="text-foreground font-semibold">{data.unique_visitors}</span> unique visitors
          </span>
        </div>
      </header>

      {allZero ? (
        <div className="rounded-2xl border border-dashed border-border bg-muted/30 p-8 text-center space-y-3" data-testid="insights-empty">
          <Sparkle weight="duotone" className="h-10 w-10 mx-auto text-primary/60" />
          <h3 className="font-semibold">No customer activity in this window yet</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Once you post a stop and customers see it on the map, this surface fills up with pin taps,
            profile views, directions clicks, and inquiries — all real engagement, no bots.
          </p>
          <Link to="/dashboard?tab=today">
            <Button variant="default" size="sm" className="gap-1">Post your next stop</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="insights-headline-grid">
            {Object.entries(HEADLINE_META).map(([k, meta]) => {
              const stat = data.headline[k] || { cur: 0, prev: 0, pct: null };
              const { Icon } = meta;
              return (
                <div
                  key={k}
                  className="rounded-2xl border border-border bg-card p-3 sm:p-4 space-y-1.5"
                  data-testid={`insights-headline-${k}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
                      <Icon weight="duotone" className={"h-3.5 w-3.5 " + meta.tone} />
                      {meta.label}
                    </div>
                    <Delta pct={stat.pct} prev={stat.prev} />
                  </div>
                  <div className="text-2xl font-bold tabular-nums leading-none">{stat.cur}</div>
                  <p className="text-[11px] text-muted-foreground leading-snug">{meta.hint}</p>
                </div>
              );
            })}
          </div>

          <div className="rounded-2xl border border-border bg-card p-4">
            <Sparkline data={data.spark} />
          </div>

          {data.top_events.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="insights-top-events">
              <div>
                <h3 className="font-semibold text-sm">Top-performing events</h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Ranked by profile views. CTR = directions taps ÷ profile views — your strongest &ldquo;ready-to-buy&rdquo; signal.
                </p>
              </div>
              <ul className="space-y-2">
                {data.top_events.map((e, idx) => (
                  <li
                    key={e.truck_id}
                    className="flex items-center gap-3 py-2 border-b border-border last:border-0"
                    data-testid={`insights-top-event-${idx}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold inline-flex items-center justify-center flex-none">
                      {idx + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{e.event_name}</div>
                      {e.address && (
                        <div className="text-[11px] text-muted-foreground truncate">{e.address}</div>
                      )}
                    </div>
                    <div className="text-right flex-none">
                      <div className="text-xs font-semibold tabular-nums">{e.profile_views} views</div>
                      <div className="text-[10px] text-muted-foreground tabular-nums">
                        {e.directions_clicks} dirs · {e.ctr_pct}% CTR
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!data.is_pro && (
            <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-4 space-y-2" data-testid="insights-pro-upsell">
              <h3 className="font-semibold text-sm inline-flex items-center gap-1">
                <Sparkle weight="duotone" className="h-4 w-4 text-primary" /> Want more depth?
              </h3>
              <p className="text-xs text-muted-foreground">
                Pro unlocks <strong>peak-hour insights</strong> (the 3-hour window when customers actually look you up),
                hourly engagement breakdowns, and a public profile editor.
              </p>
              <Link to="/pro">
                <Button size="sm" variant="default">See what Pro adds</Button>
              </Link>
            </div>
          )}
        </>
      )}
    </section>
  );
}
